// GUIDED-FLOW: who holds a new seat, chosen in "New seat". Person: once the seat exists, the
// Send seat setup dialog is offered (they get an invite and fill their part). Digital employee:
// a template (the Add-DE wizard's choices) and a name; the DE is hired onto the seat and its
// guided onboarding ("DE onboarding") opens at once for the creator to fill. The Add-DE wizard
// itself is unchanged.
import {
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import {
  choicesOf,
  mayStage,
  selfServeTemplatesQuery,
  type TemplateChoice,
} from "../de/add-de/model";
import { useOperatorName } from "../operator-name";
import { useFeature } from "../use-feature";
import { ChoiceButtons } from "./flow-entry";
import { openRunOf } from "./flow-queries";

export type HolderKind = "person" | "de";

export const HOLDER_INFO: Record<HolderKind, string> = {
  person: "A human holds this seat; they get an invite and fill their part.",
  de: "A digital employee holds this seat; you set it up in a guided flow.",
};

/** The Add-DE wizard's template choices (self-serve when the caller may deploy, managed, Custom). */
export function useDeChoices(
  api: Api,
  co: string,
  company: CompanyAccess | null,
  enabled: boolean,
): TemplateChoice[] {
  const operator = useOperatorName();
  const agentsOn = useFeature("agents", co).enabled;
  const selfServeOk = agentsOn && company !== null && mayStage(company);
  const catalog = useQuery({
    ...selfServeTemplatesQuery(api, co),
    enabled: enabled && selfServeOk,
  });
  return choicesOf(selfServeOk ? (catalog.data?.data ?? null) : null, [], operator);
}

export function SeatHolderChoice({
  holder,
  onHolder,
  choices,
  choice,
  onChoice,
  name,
  onName,
}: {
  holder: HolderKind | null;
  onHolder: (h: HolderKind) => void;
  choices: TemplateChoice[];
  choice: string;
  onChoice: (id: string) => void;
  name: string;
  onName: (name: string) => void;
}) {
  return (
    <div className="flex flex-col gap-4" data-slot="seat-holder-choice">
      <Field>
        <FieldLabel>Held by</FieldLabel>
        <ChoiceButtons
          label="Held by"
          value={holder}
          options={[
            { value: "person", label: "Person", info: HOLDER_INFO.person },
            { value: "de", label: "Digital employee (DE)", info: HOLDER_INFO.de },
          ]}
          onChange={onHolder}
        />
      </Field>
      {holder === "de" ? (
        <div className="grid gap-5 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="new-seat-de-template">Template</FieldLabel>
            <NativeSelect
              id="new-seat-de-template"
              value={choice}
              onChange={(e) => onChoice(e.target.value)}
            >
              <NativeSelectOption value="">Pick a template</NativeSelectOption>
              {choices.map((c) => (
                <NativeSelectOption key={c.id} value={c.id}>
                  {c.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            {choice ? (
              <FieldDescription>{choices.find((c) => c.id === choice)?.hint}</FieldDescription>
            ) : null}
          </Field>
          <Field>
            <FieldLabel htmlFor="new-seat-de-name">DE name</FieldLabel>
            <Input
              id="new-seat-de-name"
              value={name}
              maxLength={200}
              onChange={(e) => onName(e.target.value)}
            />
          </Field>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Hires the DE onto the seat (the template's KPIs seeded) and sends its onboarding to the caller.
 * Resolves the run's id, or null when the caller has no membership to send it to.
 */
export async function hireDeWithOnboarding(
  api: Api,
  co: string,
  input: {
    seatId: string;
    choice: TemplateChoice;
    name: string;
    me: string | null;
    /** The DE an earlier try already hired: a retry only sends the flow, never hires again. */
    hired: string | null;
    onHired: (membershipId: string) => void;
    /** One key per attempt for the hire and for the send (a retry reuses them). */
    keys: { hire: string; send: string };
  },
): Promise<string | null> {
  let deId = input.hired;
  if (!deId) {
    const de = await api.client.digitalEmployees.create({
      params: { co },
      idempotencyKey: input.keys.hire,
      body: {
        display_name: input.name.trim(),
        template_key: input.choice.templateKey,
        seat_id: input.seatId,
        seed_template_metrics: true,
      },
    });
    deId = de.membership_id;
    input.onHired(deId);
  }
  if (!input.me) return null;
  try {
    const run = await api.client.flows.send({
      params: { co },
      idempotencyKey: input.keys.send,
      body: { flow_key: "de_onboarding", seat_id: input.seatId, membership_id: input.me },
    });
    return run.id;
  } catch (e) {
    // Already sent (an earlier try that answered late): open that run.
    const open = openRunOf(e);
    if (open) return open;
    throw e;
  }
}
